import { useState } from 'react';
import { X, Ticket, Loader2 } from 'lucide-react';
import { useAppKitAccount } from '@reown/appkit/react';

import { cn } from '@/lib/utils';
import { useAuthContext } from '@/context/AuthContext';
import { useReferral } from '@/hooks/useReferral';
import {
  sanitizeInviteCodeInput,
  getInviteCodeError,
} from '@/utils/referralValidation';

interface InvitationCodeModalProps {
  open: boolean;
  onClose: () => void;
}

export function InvitationCodeModal({
  open,
  onClose,
}: InvitationCodeModalProps) {
  const { isAuthenticated } = useAuthContext();
  const { isConnected } = useAppKitAccount();
  const { bindCode, isBindingCode } = useReferral();

  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  if (!open) {
    return null;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const sanitized = sanitizeInviteCodeInput(rawValue);
    setInputValue(sanitized);
    setValidationError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isConnected || !isAuthenticated) {
      setValidationError('Please connect your wallet first');
      return;
    }

    const error = getInviteCodeError(inputValue);
    if (error) {
      setValidationError(error);
      return;
    }

    bindCode(inputValue.toUpperCase(), {
      onSuccess: () => {
        setInputValue('');
        setValidationError(null);
        onClose();
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md bg-card border border-border rounded-xl shadow-2xl animate-bounce-in">
        <button
          onClick={onClose}
          className={cn(
            'absolute top-4 right-4 p-1 rounded-full',
            'text-muted-foreground hover:text-foreground',
            'hover:bg-muted transition-colors'
          )}
        >
          <X size={20} />
        </button>

        <div className="p-6">
          <div className="text-center mb-6">
            <div className="mb-4">
              <Ticket size={48} className="mx-auto text-primary" />
            </div>
            <h2
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: 'Hyperspace Race Capsule, cursive' }}
            >
              ENTER INVITATION CODE
            </h2>
            <p
              className="text-muted-foreground text-sm"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Join Junk Fun with a 5-character invitation code from a friend.
            </p>
          </div>

          {!isConnected || !isAuthenticated ? (
            <div className="text-center py-8">
              <p
                className="text-muted-foreground mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Please connect your wallet to bind an invitation code.
              </p>
              <button
                onClick={onClose}
                className="py-2 px-8 font-medium transition-all transform hover:scale-105"
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  fontSize: '16px',
                  borderRadius: '100px',
                  background:
                    'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                  boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                  color: '#000',
                }}
              >
                CONNECT WALLET
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder="XXXXX"
                    maxLength={5}
                    className={cn(
                      'w-full p-4 text-center text-2xl font-mono uppercase',
                      'bg-muted border rounded-lg',
                      'focus:outline-none focus:ring-2 focus:ring-primary',
                      'tracking-[0.5em]',
                      validationError && 'border-red-500'
                    )}
                    style={{ fontFamily: 'Hyperspace Race Capsule, cursive' }}
                    disabled={isBindingCode}
                  />
                  {validationError && (
                    <p
                      className="text-red-500 text-sm mt-2"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {validationError}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isBindingCode || inputValue.length !== 5}
                  className={cn(
                    'w-full py-3 px-8 font-medium transition-all transform',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    !isBindingCode && 'hover:scale-105'
                  )}
                  style={{
                    fontFamily: 'Hyperspace Race Capsule, cursive',
                    fontSize: '18px',
                    borderRadius: '100px',
                    background:
                      'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                    boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                    color: '#000',
                  }}
                >
                  {isBindingCode ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="animate-spin" size={18} />
                      BINDING...
                    </span>
                  ) : (
                    'SUBMIT CODE'
                  )}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t">
                <p
                  className="text-sm text-muted-foreground text-center mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Don't have an invitation code? Get one from:
                </p>
                <div className="flex justify-center gap-4">
                  <a
                    href="https://x.com/junkfun"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    <span className="text-lg">𝕏</span>
                    <span className="text-sm">X</span>
                  </a>
                  <a
                    href="https://discord.gg/junkfun"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    <span className="text-lg">💬</span>
                    <span className="text-sm">Discord</span>
                  </a>
                  <a
                    href="https://t.me/junkfun"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    <span className="text-lg">✈️</span>
                    <span className="text-sm">Telegram</span>
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
